import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Flex } from '@atlaskit/primitives/compiled';
import { CornerStyle, RenderVariant } from '@atlassian/company-hub-common';
import { AlignmentField, CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import { ELEMENT_DEFAULTS } from '@atlassian/company-hub-common/hub-brand-palette';
import {
	HUB_CORNER_RADIUS_MAX,
	HUB_CORNER_RADIUS_MIN,
	HUB_CORNER_RADIUS_STEP,
	resolveCornerRadiusOverride,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { RangeField } from '@atlassian/company-hub-common/range-field';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../constants';
import { cardNewParametersV4 } from '../../../../controllers/use-cards-extension-parameters-and-actions';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import {
	type CardsExtensionParameters,
	CardSize,
	CardsLayout,
	Orientation,
} from '../../../../types';
import { AutoscrollToggle } from '../../fields/autoscroll-toggle';
import { BackgroundColorField } from '../../fields/background-color-field';
import { BorderColorField } from '../../fields/border-color-field';
import { OrientationField } from '../../fields/orientation-field';
import { PaddingToggle } from '../../fields/padding-toggle';
import { SizeField } from '../../fields/size-field';
import { TextColorField } from '../../fields/text-color-field';
import { messages } from '../../messages';

const styles = cssMap({
	disabledContentWrapperStyles: {
		cursor: 'not-allowed',
	},
});

type CardsConfigStylesSectionGeneralProps = {
	extensionActions: CardsActions;
	extensionParameters: CardsExtensionParameters;
	isCompanyHub: boolean;
	isDisabled: boolean;
};

export const CardsConfigStylesSectionGeneral = ({
	extensionParameters,
	extensionActions,
	isCompanyHub,
	isDisabled,
}: CardsConfigStylesSectionGeneralProps): React.JSX.Element => {
	const {
		size,
		orientation,
		alignment,
		renderVariant,
		isAutoscrollEnabled,
		borderColor,
		isPaddingEnabled,
		cornerStyle,
		cornerRadius,
		layout,
		filters,
		// to account for when backgroundColor is undefined for cards created before visual updates
		backgroundColor = cardNewParametersV4.backgroundColor,
		// to account for when textColor is undefined for cards created before visual updates
		textColor = cardNewParametersV4.textColor,
	} = extensionParameters;

	const { updateMainFormField, updateCornerStyle, updateCornerRadius } = extensionActions;

	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	// Per-element corner radius is Premium, so an un-entitled hub gets no control
	// at all rather than one it could not publish. Scoped to the new flow: the
	// legacy rounded/squared toggle below is not a radius setting and stays.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();
	const theming = useHubTheming();
	// Show the colour the canvas actually paints: cards re-derive an
	// auto-managed black/white against the live background at render time.
	// Resolved from the raw ADF values, exactly as `cards-content` does.
	const resolveTextColor = useHubAutoTextColor('cards-extension');
	const displayedTextColor = fg('hubs_config_panel_text_color_parity')
		? (resolveTextColor({
				backgroundColor: extensionParameters.backgroundColor,
				textColor: extensionParameters.textColor,
				defaultRef: ELEMENT_DEFAULTS['cards.bg'],
			}) ?? textColor)
		: textColor;

	const handleCornerRadius = useCallback(
		(value: number) => updateCornerRadius(value),
		[updateCornerRadius],
	);

	const isUnpopulatedDynamicCards = renderVariant === RenderVariant.DYNAMIC && !filters;
	const shouldShowAutoscrollToggle =
		isCompanyHub && orientation === Orientation.ROW && !isUnpopulatedDynamicCards;

	return (
		<Flex
			testId="styles-general-section"
			direction="column"
			gap="space.200"
			xcss={isDisabled && styles.disabledContentWrapperStyles}
		>
			<SizeField
				sizeParameter={size}
				renderVariant={renderVariant}
				updateMainFormField={updateMainFormField}
				showUpdatedStyles
			/>
			<OrientationField
				orientation={orientation}
				renderVariant={renderVariant}
				updateMainFormField={updateMainFormField}
				showUpdatedStyles
			/>
			{shouldShowAutoscrollToggle && (
				<AutoscrollToggle
					isAutoscrollEnabled={isAutoscrollEnabled}
					renderVariant={renderVariant}
					updateMainFormField={updateMainFormField}
				/>
			)}
			<BackgroundColorField
				textColor={textColor}
				backgroundColor={backgroundColor}
				isCompanyHub={isCompanyHub}
				updateMainFormField={updateMainFormField}
			/>
			<TextColorField
				textColor={displayedTextColor}
				backgroundColor={backgroundColor}
				isCompanyHub={isCompanyHub}
				updateMainFormField={updateMainFormField}
			/>
			{layout === CardsLayout.FIXED_IMAGE_HEIGHT && size !== CardSize.SMALL && (
				<>
					<BorderColorField
						borderColor={borderColor}
						isCompanyHub={isCompanyHub}
						updateMainFormField={updateMainFormField}
					/>
					<PaddingToggle
						isPaddingEnabled={isPaddingEnabled}
						renderVariant={renderVariant}
						updateMainFormField={updateMainFormField}
					/>
				</>
			)}
			{isCompanyHub &&
				// The new-flow slider is Premium-only; the legacy toggle is not a radius
				// setting, so it is left alone.
				!(isCornerRadiusEnabled && isCornerRadiusLocked) &&
				(isCornerRadiusEnabled ? (
					<RangeField
						label={messages.cornerRadiusLabel}
						minValue={HUB_CORNER_RADIUS_MIN}
						maxValue={HUB_CORNER_RADIUS_MAX}
						step={HUB_CORNER_RADIUS_STEP}
						value={resolveHubCornerRadius(
							resolveCornerRadiusOverride(cornerRadius, cornerStyle) ?? theming?.cornerRadius,
						)}
						updateRangeValue={handleCornerRadius}
						analyticSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
						prefix="cardsCornerRadius"
						unit="px"
					/>
				) : (
					<CornerStyleConfig<CornerStyle>
						cornerStyleValue={cornerStyle ?? CornerStyle.ROUNDED}
						updateCornerStyle={updateCornerStyle}
						roundCornerValue={CornerStyle.ROUNDED}
						squareCornerValue={CornerStyle.SQUARED}
					/>
				))}
			{orientation === Orientation.STACKED && (
				<AlignmentField
					selectedAlignment={alignment}
					renderVariant={renderVariant}
					updateMainFormField={updateMainFormField}
					source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
				/>
			)}
		</Flex>
	);
};
